/**
 * Phase 128 P2 — ArenaX Capability gating, canonical version.
 *
 * Doctrine (from CEO):
 *   - Role ↔ organizational identity (Out-Play tenancy: academy_owner,
 *     coach, employee, super_admin, student, parent)
 *   - Capability ↔ ArenaX participation power (publish_venue,
 *     manage_academy, host_tournament, create_league, activate_as_host,
 *     book_slot, create_squad)
 *
 * Frontend code should NEVER infer capabilities from `role`, operator
 * profile heuristics, or local assumptions.  It MUST ask the canonical
 * surface: `GET /api/arenax/me/capabilities`.  This module exposes:
 *
 *   - `useArenaxCapabilities()`   — hook returning { caps, hasCap, role,
 *                                    loading, refresh }
 *   - `<CapabilityGate cap="X">`  — declarative gate component
 *
 * Use `<CapabilityGate>` for ArenaX-touching routes (host, organizer,
 * tournament).  Keep `<RoleGate>` for Out-Play tenant admin routes
 * (settings, fees, CRM, HRMS, payroll).
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const ArenaxCapabilityContext = createContext(null);

export function ArenaxCapabilityProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    caps: [],
    capsMeta: {},
    role: null,
    loading: true,
    error: null,
  });

  const fetchCaps = useCallback(async () => {
    if (!user) {
      setState({ caps: [], capsMeta: {}, role: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get('/arenax/me/capabilities');
      setState({
        caps: r.data?.capabilities || [],
        capsMeta: r.data?.capabilities_meta || {},
        role: r.data?.role || null,
        loading: false,
        error: null,
      });
    } catch (e) {
      setState({
        caps: [], capsMeta: {}, role: user?.role || null,
        loading: false, error: e.message || 'fetch failed',
      });
    }
  }, [user]);

  useEffect(() => {
    fetchCaps();
  }, [fetchCaps]);

  const hasCap = useCallback((cap) => {
    if (!cap) return true;
    if (state.role === 'super_admin') return true; // platform bypass
    return state.caps.includes(cap);
  }, [state.caps, state.role]);

  const value = {
    caps: state.caps,
    capsMeta: state.capsMeta,
    role: state.role,
    loading: state.loading,
    error: state.error,
    hasCap,
    refresh: fetchCaps,
  };

  return (
    <ArenaxCapabilityContext.Provider value={value}>
      {children}
    </ArenaxCapabilityContext.Provider>
  );
}

export function useArenaxCapabilities() {
  const ctx = useContext(ArenaxCapabilityContext);
  if (!ctx) {
    // Fail-soft: if a component renders outside the provider, return a
    // permissive shape so the page doesn't crash.  Render a console
    // hint in dev so it's easy to spot.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('useArenaxCapabilities() called outside ArenaxCapabilityProvider — returning permissive fallback');
    }
    return {
      caps: [], capsMeta: {}, role: null, loading: false, error: null,
      hasCap: () => false, refresh: async () => {},
    };
  }
  return ctx;
}

/**
 * <CapabilityGate cap="publish_venue">  — gate ArenaX routes by capability.
 *
 * Renders `<Loader/>` while caps are loading, then either children (if
 * granted) or a redirect to a sensible fallback.
 *
 * Props:
 *   - `cap`         single capability slug to require
 *   - `anyOf`       array of capabilities — any one is enough
 *   - `fallback`    redirect path if not granted (default: '/arenax')
 *   - `children`    protected UI
 */
export function CapabilityGate({ cap, anyOf, fallback = '/arenax', children }) {
  const { loading, hasCap } = useArenaxCapabilities();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]"
           data-testid="arenax-capability-gate-loading">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const ok = anyOf
    ? anyOf.some((c) => hasCap(c))
    : hasCap(cap);

  if (!ok) {
    return <Navigate to={fallback} replace />;
  }
  return children;
}

/**
 * <CapabilityShow cap="publish_venue">  — conditional show/hide for UI.
 * Use in headers, navbars, dropdowns.  Doesn't redirect — just renders
 * nothing if the capability is absent.
 */
export function CapabilityShow({ cap, anyOf, fallback = null, children }) {
  const { hasCap, loading } = useArenaxCapabilities();
  if (loading) return null;
  const ok = anyOf ? anyOf.some((c) => hasCap(c)) : hasCap(cap);
  return ok ? children : fallback;
}
